import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Circle, Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { showConfirm } from '@/stores/confirmer';

const validTabsArray = [
    'DistributionBoard',
    'FinalCircuit',
    'DistributionBoardFile',
    'DistributionBoardDevice',
    'RisoMeasurement',
    'PVInverter',
    'PVString',
] as const;
export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/observations/',

)({
    component: RouteComponent,
    validateSearch: z.object({
        tab: z.enum(validTabsArray).optional(),
        search: z.string().optional(),
    }),
});

type ListObservationOption = components['schemas']['ListElectricalObservationStandardTextItemQueryResultItem'];

function ObservationsModal({
    isOpen,
    setIsOpen,
    observation,
    objectkinds,
    severityObservations,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    observation?: ListObservationOption;
    objectkinds: RadioGroupOption<ListObservationOption['ObjectKind']>[];
    severityObservations: Record<string, string>;
}) {
    const { t } = useTranslation('dashboard');
    const { tab } = Route.useSearch();
    const { organizationId } = Route.useParams();
    const [loading, setIsLoading] = useState<boolean>(false);

    const DEFAULT_VALUES: ListObservationOption = {
        ElectricalObservationStandardTextItemId: observation?.ElectricalObservationStandardTextItemId ?? '',
        Description: observation?.Description ?? '',
        Order: observation?.Order ?? null,
        ObjectKind: validTabsArray.includes(observation?.ObjectKind as typeof validTabsArray[number])
            ? observation?.ObjectKind as typeof validTabsArray[number]
            : (validTabsArray.includes(tab as typeof validTabsArray[number]) ? tab as typeof validTabsArray[number] : 'DistributionBoard'),
        TextKind: observation?.TextKind ?? 'Remark',
    };

    const [scopeList, setScopeList] = useState(() => {
        const inspectionKinds = ['Scope8', 'Scope10', 'Scope12', 'Nen1010', 'Nen3140'] as const;
        return inspectionKinds.map((kind) => {
            const base = { Severe: false, Serious: false, Minor: false, Remark: false };
            let groupKind: string | null = null;
            if (observation?.Reports) {
                observation.Reports.forEach((report) => {
                    if (report.InspectionKind === kind && report.CodeKind) {
                        (base as any)[report.CodeKind] = true;
                        groupKind = report.GroupKind ?? null;
                    }
                });
            }
            return {
                Inspection: kind,
                GroupKind: groupKind,
                ...base,
            };
        });
    });

    const changeScopeStatus = useCallback((inspection: string, severity: 'Severe' | 'Serious' | 'Minor' | 'Remark') => {
        setScopeList(prevList =>
            prevList.map((row) => {
                if (row.Inspection !== inspection)
                    return row;

                const trueKey = Object.entries(row).find(([_, value]) => value === true)?.[0];

                return {
                    ...row,
                    Severe: false,
                    Serious: false,
                    Minor: false,
                    Remark: false,
                    [severity]: trueKey !== severity,
                };
            }),
        );
    }, []);

    const columns = useMemo(() => [
        {
            accessorKey: 'Inspection',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.table.report.header.inspection')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="medium">
                    {t(`dashboard.settings.observations.header.table.${row?.original?.Inspection.toLowerCase()}`)}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Severe',
            header: () => (
                <DataTable.Header>
                    {severityObservations.Severe}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="bold">
                    <Checkbox
                        checked={row?.original?.Severe}
                        onClick={() => changeScopeStatus(row?.original?.Inspection, 'Severe')}
                        className={
                            cn(
                                `rounded-3xl p-2 ${row?.original?.Severe && '!!bg-brand-solid'}`,
                            )
                        }
                        icon={<Circle fill="white" className="absolute" size={6} />}

                    >

                    </Checkbox>

                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Serious',
            header: () => (
                <DataTable.Header>
                    {severityObservations.Serious}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="bold">
                    <Checkbox
                        checked={row?.original?.Serious}
                        onClick={() => changeScopeStatus(row?.original?.Inspection, 'Serious')}
                        className={
                            cn(
                                `rounded-3xl p-2 ${row?.original?.Severe && '!!bg-brand-solid'}`,
                            )
                        }
                        icon={<Circle fill="white" className="absolute" size={6} />}
                    />
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Minor',
            header: () => (
                <DataTable.Header>
                    {severityObservations.Minor}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="bold">
                    <Checkbox
                        checked={row?.original?.Minor}
                        onClick={() => changeScopeStatus(row?.original?.Inspection, 'Minor')}
                        className={
                            cn(
                                `rounded-3xl p-2 ${row?.original?.Severe && '!!bg-brand-solid'}`,
                            )
                        }
                        icon={<Circle fill="white" className="absolute" size={6} />}
                    />
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Remark',
            header: () => (
                <DataTable.Header>
                    {severityObservations.Remark}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="bold">
                    <Checkbox
                        checked={row?.original?.Remark}
                        onClick={() => changeScopeStatus(row?.original?.Inspection, 'Remark')}
                        className={
                            cn(
                                `rounded-3xl p-2 ${row?.original?.Severe && '!!bg-brand-solid'}`,
                            )
                        }
                        icon={<Circle fill="white" className="absolute" size={6} />}
                    />
                </DataTable.Cell>
            ),
        },
    ], [changeScopeStatus, severityObservations.Minor, severityObservations.Remark, severityObservations.Serious, severityObservations.Severe, t]);

    const tableData
        = () => scopeList
    ;
    const updateObservation = api.useMutation(
        'put',
        '/api/v2/electrical/observationstandardtexts/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.observations.listObservations({ organizationId })).then(() => {
                    toast.success(t('dashboard.settings.observations.message.editSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditElectricalObservationStandardTextItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/observationstandardtexts/items/edit' },
                );
            },
            onMutate: () => setIsLoading(true),

        },
    );

    const createObservation = api.useMutation(
        'post',
        '/api/v2/electrical/observationstandardtexts/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.observations.listObservations({ organizationId })).then(() => {
                    toast.success(t('dashboard.settings.observations.message.createSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalObservationStandardTextItemCommandResult',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/observationstandardtexts/items/create' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),

        },
    );

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: async (value) => {
            if (observation) {
                await updateObservation.mutateAsync({
                    body: {
                        Description: value.value.Description ?? '',
                        ElectricalObservationStandardTextItemId: observation.ElectricalObservationStandardTextItemId ?? '',
                        Order: observation.Order ?? 1,
                        Reports: scopeList.map((row) => {
                            const activeKind = (row.Severe && 'Severe') || (row.Serious && 'Serious') || (row.Minor && 'Minor') || (row.Remark && 'Remark') || null;
                            return {
                                InspectionKind: row.Inspection,
                                GroupKind: row.GroupKind ?? null,
                                CodeKind: activeKind,
                            };
                        }),
                    },
                });
            }
            else {
                await createObservation.mutateAsync({
                    body: {
                        Description: value.value.Description ?? '',
                        OrganizationId: organizationId,
                        TextKind: 'Remark',
                        ObjectKind: value.value.ObjectKind ?? 'DistributionBoard',
                        Order: 1,
                        Reports: scopeList.map((row) => {
                            const activeKind = (row.Severe && 'Severe') || (row.Serious && 'Serious') || (row.Minor && 'Minor') || (row.Remark && 'Remark') || null;
                            return {
                                InspectionKind: row.Inspection,
                                GroupKind: row.GroupKind ?? null,
                                CodeKind: activeKind,
                            };
                        }),
                    },
                });
            }
        },
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{!observation ? t('dashboard.settings.observations.create.title') : t('dashboard.settings.observations.update.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                        <form.AppField
                            name="Description"
                            validators={{
                                onChange: validators.createElectricalObservationStandardTextItemCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.observations.table.observation')} />
                                    <field.Textarea style={{ height: '150px' }} placeholder={t('dashboard.settings.observations.table.observationPlaceholder')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />
                        {!observation
                            ? (
                                    <form.AppField
                                        name="ObjectKind"
                                        validators={{
                                            onChange: validators.createElectricalObservationStandardTextItemCommand.objectKind,
                                        }}
                                        children={field => (
                                            <field.Label asDiv>
                                                <field.Label.Title label={t('dashboard.settings.observations.create.kindOfObservation')} />
                                                <field.Popover closeOnChange={true}>
                                                    <field.RadioGroup options={objectkinds}>
                                                        <field.Popover.Trigger asChild>
                                                            <field.RadioGroup.Trigger placeholder={t('dashboard.users.table.rolePlaceholder')} />
                                                        </field.Popover.Trigger>
                                                        <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                            <field.RadioGroup options={objectkinds} />
                                                        </field.Popover.Content>
                                                    </field.RadioGroup>
                                                </field.Popover>
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    >
                                    </form.AppField>
                                )
                            : null}
                        <DataTable
                            className="rounded-x mb-3"
                            columns={columns}
                            data={tableData()}
                            pageSize={6}
                        />
                        <div className="w=full flex flex-row gap-2">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

function RouteComponent() {
    const { organizationId } = Route.useParams();
    const [isOpenObservations, setOpenObservations] = useState<boolean>(false);
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch();
    const [selectedOpservation, setSelectedObservation] = useState<ListObservationOption | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const severityObservations = useMemo<Record<string, string>>(() => ({
        Severe: t('dashboard.settings.observations.header.table.severse'),
        Serious: t('dashboard.settings.observations.header.table.serious'),
        Minor: t('dashboard.settings.observations.header.table.minor'),
        Remark: t('dashboard.settings.observations.header.table.remark'),
    }), [t]);

    const recommendationList = useQuery(
        portaalQueryOptions.observations.listObservations({ organizationId }),
    );

    if (recommendationList.isFetched && recommendationList.isError) {
        const error = recommendationList.error;
        logger.error(
            {
                system: 'DeleteElectricalInspectionNormItemCommand',
                error,
            },
            { apiHttpMethod: 'Get', apiUrl: '/api/v2/electrical/observationstandardtexts/items/list' },
        );
    };

    const tabsKind: RadioGroupOption<ListObservationOption['ObjectKind']>[] = [
        {
            value: 'DistributionBoard',
            label: t('dashboard.settings.recommendations.tabs.distributionBoard'),
        },
        {
            value: 'FinalCircuit',
            label: t('dashboard.settings.recommendations.tabs.finalCircuit'),
        },
        {
            value: 'PVInverter',
            label: t('dashboard.settings.recommendations.tabs.PVInverter'),
        },
    ];

    const deleteObservation = api.useMutation(
        'delete',
        '/api/v2/electrical/observationstandardtexts/items/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.observations.listObservations({ organizationId })).then(() => {
                    toast.success(t('dashboard.settings.observations.message.deleteSuccess'));
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalObservationStandardTextItemCommandResult',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/observationstandardtexts/items/create' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
        },
    );

    const [activeTab, setActiveTab] = useState<string>(tabsKind[0].value ?? '');

    const switchTab = (tab: string) => {
        setActiveTab(tab);
    };
    const columns = useMemo(() => [
        {
            accessorKey: 'Description',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.observation')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationOption } }) => (
                <DataTable.Cell variant="medium">
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.ElectricalObservationStandardTextItemId ? null : row?.original?.ElectricalObservationStandardTextItemId ?? null)}
                    >
                        {expandedId === row?.original?.ElectricalObservationStandardTextItemId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <div className="max-w-xl">
                        {row?.original?.Description || '-'}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Scope8',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.header.table.scope8')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationOption } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Reports
                        ?.filter(x => x.InspectionKind === 'Scope8')
                        ?.map(x => severityObservations?.[x.CodeKind as string] ?? x.CodeKind)
                        ?.join(', ') || '-'}

                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Scope10',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.header.table.scope10')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationOption } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Reports
                        ?.filter(x => x.InspectionKind === 'Scope10')
                        ?.map(x => severityObservations?.[x.CodeKind as string] ?? x.CodeKind)
                        ?.join(', ') || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Scope12',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.header.table.scope12')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationOption } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Reports
                        ?.filter(x => x.InspectionKind === 'Scope12')
                        ?.map(x => severityObservations?.[x.CodeKind as string] ?? x.CodeKind)
                        ?.join(', ') || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Nen1010',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.header.table.nen1010')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationOption } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Reports
                        ?.filter(x => x.InspectionKind === 'Nen1010')
                        ?.map(x => severityObservations?.[x.CodeKind as string] ?? x.CodeKind)
                        ?.join(', ') || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Nen3140',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.observations.header.table.nen3140')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationOption } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Reports
                        ?.filter(x => x.InspectionKind === 'Nen3140')
                        ?.map(x => severityObservations?.[x.CodeKind as string] ?? x.CodeKind)
                        ?.join(', ') || '-'}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: ListObservationOption } }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.settings.observations.message.confirmDelete'))) {
                                    await deleteObservation.mutateAsync({
                                        params: {
                                            query: {
                                                ElectricalObservationStandardTextItemId: row?.original?.ElectricalObservationStandardTextItemId ?? '',
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setSelectedObservation(row?.original);
                                setOpenObservations(true);
                            }}
                        >
                            <Edit className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [t, expandedId, severityObservations, deleteObservation]);

    const tableColumns = useMemo(
        () => recommendationList.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, recommendationList.isLoading],
    );

    const tableData = useMemo<ListObservationOption[]>(
        () => {
            if (recommendationList.isError) {
                toast.error(recommendationList.error?.Message);
                return [];
            }
            const searchTerm = search.search?.toLowerCase() || '';

            return recommendationList.data?.Items?.filter((norm) => {
                if (norm.ObjectKind !== activeTab)
                    return false;
                if (!searchTerm)
                    return true;

                return [
                    norm.Description || '',
                ].some(field => field?.toString().toLowerCase().includes(searchTerm));
            }) || [];
        },
        [recommendationList.isError, recommendationList.data?.Items, recommendationList.error?.Message, search.search, activeTab],
    );

    if (recommendationList.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <div className="w-full">
                <Button
                    variant="link"
                    className="flex w-full mb-3 sm:hidden items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                    onClick={() => {
                        setSelectedObservation(null);
                        setOpenObservations(true);
                    }}
                >
                    <PlusIcon className="w-5 h-5" />
                    {t('dashboard.settings.observations.observation')}
                </Button>
                <DataTable.TableHead
                    title={t('dashboard.settings.observations.title')}
                    subtitle={t('dashboard.settings.observations.subtitle')}
                    search={Route}
                />
                {/* buttons lg+ */}
                <div>
                    {/* buttons lg+ */}
                    <div className="flex py-3 border px-5 w-full">
                        <div className="flex flex-col sm:flex-row md:gap-0  w-full md:w-auto">
                            <div className="sm:hidden w-full">
                                <DropdownMenu>
                                    <DropdownMenuTrigger className="flex items-center gap-2 border-2 border-primary/60 px-3 rounded-lg h-10 bg-background-primary text-primary hover:bg-primary/10 justify-center w-full">
                                        <span>{tabsKind.find(tab => tab.value === activeTab)?.label || tabsKind[0].label}</span>
                                        <ChevronDown className="w-5 h-5" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="flex flex-col w-[calc(100vw-2rem)]" align="start">
                                        {tabsKind.map(item => (
                                            <DropdownMenuItem
                                                key={item.value}
                                                className="p-3 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer text-base"
                                                asChild
                                            >
                                                <Link
                                                    to="/dashboard/organizations/$organizationId/settings/observations"
                                                    params={{ organizationId }}
                                                    search={{
                                                        tab: item.value ?? undefined,
                                                    }}
                                                    onClick={() => switchTab(item.value ?? '')}
                                                >
                                                    {item.label}
                                                </Link>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>

                            {tabsKind.map((item, index) => (
                                <Link
                                    key={item.value}
                                    to="/dashboard/organizations/$organizationId/settings/observations"
                                    params={{ organizationId }}
                                    search={{
                                        tab: item.value ?? undefined,
                                        search: search.search,
                                    }}
                                    className="hidden sm:block"
                                >
                                    <Button
                                        onClick={() => switchTab(item.value ?? '')}
                                        style={
                                            index === 0
                                                ? { borderRadius: '10px 0px 0px 10px ', borderLeft: '1px solid hsl(var(--border-primary)' }
                                                : index === tabsKind.length - 1
                                                    ? { borderRadius: '0px 10px 10px 0px' }
                                                    : { borderRadius: '0px' }
                                        }
                                        className={`border-border-primary border-t border-r border-b bg-background-active hover:bg-active ${item.value === activeTab ? ' text-secondary_hover font-bold' : 'text-secondary-700 bg-background-primary text-sm'}`}
                                    >
                                        {item.label}
                                    </Button>
                                </Link>
                            ))}
                        </div>
                        <div className="flex flex-1 justify-end items-end">
                            <div className="sm:flex flex-row-reverse gap-4 hidden">
                                <Button
                                    variant="link"
                                    className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                                    onClick={() => {
                                        setSelectedObservation(null);
                                        setOpenObservations(true);
                                    }}
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    {t('dashboard.settings.observations.title')}
                                </Button>
                            </div>
                        </div>
                    </div>

                </div>
                <DataTable
                    className="rounded-tl-none rounded-tr-none"
                    columns={tableColumns}
                    data={tableData}
                    pageSize={10}
                    expandedRow={row =>
                        expandedId && expandedId === row?.original?.ElectricalObservationStandardTextItemId
                            ? (
                                    <ExpandedRowsMobile columns={columns} data={row.original} />
                                )
                            : null}
                />
            </div>
            {isOpenObservations && (
                <ObservationsModal
                    isOpen={isOpenObservations}
                    setIsOpen={setOpenObservations}
                    observation={selectedOpservation ?? undefined}
                    objectkinds={tabsKind}
                    severityObservations={severityObservations}
                />
            ) }
        </div>
    );
}
